import * as React from 'react';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { cn } from '../lib/utils';
import { buttonVariants } from './button';

export interface PaginationProps {
  current: number;
  total: number;
  pageSize: number;
  onChange?: (page: number) => void;
  showTotal?: (total: number, range: [number, number]) => React.ReactNode;
  className?: string;
}

const Pagination = React.forwardRef<HTMLElement, PaginationProps>(
  ({ current, total, pageSize, onChange, showTotal, className }, ref) => {
    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    const getRange = (): [number, number] => {
      if (total === 0) return [0, 0];
      const start = Math.min((current - 1) * pageSize + 1, total);
      const end = Math.min(current * pageSize, total);
      return [start, end];
    };

    const getPageNumbers = (): (number | 'ellipsis')[] => {
      const pages: (number | 'ellipsis')[] = [];

      if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i++) pages.push(i);
        return pages;
      }

      pages.push(1);
      if (current > 3) pages.push('ellipsis');

      const start = Math.max(2, current - 1);
      const end = Math.min(totalPages - 1, current + 1);
      for (let i = start; i <= end; i++) pages.push(i);

      if (current < totalPages - 2) pages.push('ellipsis');
      pages.push(totalPages);

      return pages;
    };

    return (
      <nav
        ref={ref}
        role="navigation"
        aria-label="Pagination"
        className={cn('flex items-center gap-1', className)}
      >
        {showTotal && (
          <span className="mr-4 text-sm text-muted-foreground">
            {showTotal(total, getRange())}
          </span>
        )}

        <PaginationButton
          onClick={() => onChange?.(current - 1)}
          disabled={current <= 1}
          aria-label="Go to previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </PaginationButton>

        {getPageNumbers().map((page, i) =>
          page === 'ellipsis' ? (
            <span
              key={`ellipsis-${i}`}
              aria-hidden
              className={cn(
                buttonVariants({ variant: 'ghost', size: 'icon' }),
                'h-9 w-9 cursor-default hover:bg-transparent'
              )}
            >
              <MoreHorizontal className="h-4 w-4" />
            </span>
          ) : (
            <PaginationButton
              key={page}
              onClick={() => onChange?.(page)}
              aria-current={current === page ? 'page' : undefined}
              isActive={current === page}
            >
              {page}
            </PaginationButton>
          )
        )}

        <PaginationButton
          onClick={() => onChange?.(current + 1)}
          disabled={current >= totalPages}
          aria-label="Go to next page"
        >
          <ChevronRight className="h-4 w-4" />
        </PaginationButton>
      </nav>
    );
  }
);
Pagination.displayName = 'Pagination';

interface PaginationButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isActive?: boolean;
}

const PaginationButton = React.forwardRef<
  HTMLButtonElement,
  PaginationButtonProps
>(({ className, isActive, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      buttonVariants({
        variant: isActive ? 'outline' : 'ghost',
        size: 'icon',
      }),
      'h-9 w-9',
      isActive && 'border-input bg-background hover:bg-accent hover:text-accent-foreground',
      className
    )}
    {...props}
  />
));
PaginationButton.displayName = 'PaginationButton';

export { Pagination };
